import { useMemo } from 'react';
import { useTimelinePosition } from '../components/timeline-position-state';
import { useProject } from '../store/projectContext';

/**
 * Returns which scene indices should have their <video> elements actively mounted.
 * Only scenes within ±BUFFER of the current scene get real video elements;
 * the rest get lightweight placeholders to avoid exhausting browser media resources.
 *
 * @param {number} buffer — how many scenes before/after current to keep mounted (default 2)
 * @returns {{ activeSceneIndex: number, isSceneNearby: (index: number) => boolean, nearbySet: Set<number> }}
 */
const BUFFER = 2;

export function useActiveSceneWindow(buffer = BUFFER) {
  const project = useProject();
  const frame = useTimelinePosition();
  const fps = project?.fps || 30;
  const scenes = project?.scenes;

  const { activeSceneIndex, nearbySet } = useMemo(() => {
    if (!scenes || scenes.length === 0) {
      return { activeSceneIndex: -1, nearbySet: new Set() };
    }

    // Find current scene based on frame position
    let cumulativeFrame = 0;
    let currentIndex = 0;
    for (let i = 0; i < scenes.length; i++) {
      const durationInFrames = Math.round((scenes[i].duration || 4) * fps);
      if (frame >= cumulativeFrame && frame < cumulativeFrame + durationInFrames) {
        currentIndex = i;
        break;
      }
      cumulativeFrame += durationInFrames;
      // If we're past all scenes, clamp to last scene
      if (i === scenes.length - 1) {
        currentIndex = i;
      }
    }

    // Build a set of scene indices that should have active video elements
    const set = new Set();
    const start = Math.max(0, currentIndex - buffer);
    const end = Math.min(scenes.length - 1, currentIndex + buffer);
    for (let i = start; i <= end; i++) {
      set.add(i);
    }

    return { activeSceneIndex: currentIndex, nearbySet: set };
  }, [scenes, frame, fps, buffer]);

  const isSceneNearby = useMemo(() => {
    return (index) => nearbySet.has(index);
  }, [nearbySet]);

  return { activeSceneIndex, isSceneNearby, nearbySet };
}
